import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Heart, Users, Star, Award } from 'react-feather';

const AboutPage = () => {
  const navigate = useNavigate();

  return (
    <div className="bg-white min-h-screen">
      {/* Header */}
      <header className="bg-gradient-to-r from-pink-500 to-purple-600 text-white">
        <div className="max-w-7xl mx-auto py-16 px-4 sm:py-24 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center"
          >
            <h1 className="text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
              About Us
            </h1>
            <p className="mt-6 max-w-3xl mx-auto text-xl">
              We're dedicated to making wedding planning beautiful, stress-free, and unforgettable
            </p>
          </motion.div>
        </div>
      </header>

      {/* Our Story */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="lg:grid lg:grid-cols-2 lg:gap-8 items-center"
          >
            <div>
              <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
                Our Story
              </h2>
              <p className="mt-6 text-lg text-gray-500">
                Wedding App was founded in 2023 with a simple vision: to transform wedding planning from a stressful experience into a joyful journey. Our founders, having gone through their own wedding planning challenges, recognized the need for a comprehensive, user-friendly platform that would simplify the process.
              </p>
              <p className="mt-4 text-lg text-gray-500">
                What began as a small project has now blossomed into a thriving platform serving thousands of couples worldwide. We remain committed to our original mission: making wedding planning accessible, enjoyable, and memorable for everyone.
              </p>
            </div>
            <div className="mt-10 lg:mt-0">
              <div className="bg-pink-50 rounded-lg overflow-hidden shadow-xl transform transition-all hover:scale-105">
                <img
                  src="/api/placeholder/600/400"
                  alt="Team working together"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl font-extrabold tracking-tight text-gray-900 sm:text-4xl">
              Our Values
            </h2>
            <p className="mt-4 max-w-2xl mx-auto text-lg text-gray-500">
              The principles that guide everything we do
            </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 sm:grid-cols-2 lg:grid-cols-4">
            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-lg overflow-hidden p-6"
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-pink-500 text-white mb-4">
                <Heart className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Passion</h3>
              <p className="mt-2 text-base text-gray-500">
                We're passionate about weddings and committed to making each couple's experience exceptional.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-lg overflow-hidden p-6"
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-pink-500 text-white mb-4">
                <Users className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Inclusivity</h3>
              <p className="mt-2 text-base text-gray-500">
                We celebrate all love stories and create tools that work for couples of all backgrounds.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-lg overflow-hidden p-6"
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-pink-500 text-white mb-4">
                <Star className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Excellence</h3>
              <p className="mt-2 text-base text-gray-500">
                We strive for excellence in every feature, design element, and customer interaction.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white rounded-lg shadow-lg overflow-hidden p-6"
            >
              <div className="flex items-center justify-center h-12 w-12 rounded-md bg-pink-500 text-white mb-4">
                <Award className="h-6 w-6" />
              </div>
              <h3 className="text-lg font-medium text-gray-900">Innovation</h3>
              <p className="mt-2 text-base text-gray-500">
                We continuously innovate to bring couples the most helpful planning tools and features.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="bg-gradient-to-r from-purple-600 to-pink-500 text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl font-bold mb-8">
              Join Thousands of Happy Couples
            </h2>
            <p className="text-xl max-w-3xl mx-auto mb-10">
              Start planning your dream wedding today with the tools and support you need
            </p>
            <motion.button
              onClick={() => navigate('/register')}
              className="inline-flex items-center bg-white text-pink-600 font-bold px-8 py-4 rounded-full shadow-lg text-lg"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get Started For Free
            </motion.button>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="text-gray-400">© {new Date().getFullYear()} Wedding App. All rights reserved.</p>
          <div className="mt-4">
            <button 
              onClick={() => navigate('/')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Home
            </button>
            <button 
              onClick={() => navigate('/contact')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Contact
            </button>
            <button 
              onClick={() => navigate('/privacy')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Privacy Policy
            </button>
            <button 
              onClick={() => navigate('/terms')}
              className="text-gray-400 hover:text-pink-300 transition-colors mx-2"
            >
              Terms of Service
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default AboutPage;